import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { environment } from '../../enviroments/environment';
import { WindowService } from './window.service';

type TelemetryProps = Record<string, string | number | boolean | null | undefined>;
type TelemetryMeasurements = Record<string, number>;

interface TelemetryRuntimeConfig {
  appInsightsConnectionString?: string;
}

interface TelemetryWindow extends Window {
  __TELEMETRY_CONFIG__?: TelemetryRuntimeConfig;
}

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  // Telemetry: centralized frontend telemetry context.
  private appInsights?: ApplicationInsights;
  private clickAnalyticsPlugin = new ClickAnalyticsPlugin();
  private initialized = false;
  private readonly commonContext: TelemetryProps = {};
  // Telemetry: normalized client context (avoid raw userAgent in telemetry payload).
  private clientContext: TelemetryProps = {};

  constructor(private windowService: WindowService) {
    this.clientContext = this.buildClientContext();
    this.initialize();
  }

  setCommonContext(context: TelemetryProps): void {
    Object.assign(this.commonContext, context);
  }

  trackEvent(name: string, properties: TelemetryProps = {}, measurements: TelemetryMeasurements = {}): void {
    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    const mergedProps = this.withContext(properties);

    if (!this.appInsights) {
      this.debugLog(`⏭️ TELEMETRY-SKIPPED event=${name} reason=appInsights_not_initialized`, mergedProps, measurements);
      return;
    }

    this.appInsights.trackEvent({ name }, mergedProps);
    Object.entries(measurements).forEach(([metricName, metricValue]) => {
      this.appInsights?.trackMetric({
        name: `${name}.${metricName}`,
        average: metricValue,
      }, mergedProps);
    });
    this.debugLog(`✅ TELEMETRY-REGISTERED event=${name}${this.buildTelemetrySummary(mergedProps, measurements)}`, mergedProps, measurements);
  }

  trackPageView(name: string, uri: string, properties: TelemetryProps = {}): void {
    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    const mergedProps = this.withContext(properties);

    if (!this.appInsights) {
      this.debugLog(`⏭️ TELEMETRY-SKIPPED pageView=${name} reason=appInsights_not_initialized`, mergedProps);
      return;
    }

    this.appInsights.trackPageView({
      name,
      uri,
      properties: mergedProps,
    });
    this.debugLog(`✅ TELEMETRY-REGISTERED pageView=${name} uri=${uri}${this.buildTelemetrySummary(mergedProps)}`, mergedProps);
  }

  trackException(error: unknown, properties: TelemetryProps = {}): void {
    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    const mergedProps = this.withContext(properties);

    if (!this.appInsights) {
      this.debugLog('⏭️ TELEMETRY-SKIPPED exception reason=appInsights_not_initialized', mergedProps, error);
      return;
    }

    this.appInsights.trackException({ exception: error as Error }, mergedProps);
    this.debugLog('✅ TELEMETRY-REGISTERED exception', mergedProps, error);
  }

  trackApiCallResult(
    apiName: string,
    url: string,
    durationMs: number,
    success: boolean,
    statusCode?: number,
    properties: TelemetryProps = {}
  ): void {
    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    const host = this.getHost(url);
    this.debugLog(
      `🔎 TELEMETRY-DEBUG apiName=${apiName} success=${success} durationMs=${Math.round(durationMs)} statusCode=${statusCode ?? ''}`,
      { urlHost: host, ...properties }
    );
    this.trackEvent('api_call_result', {
      apiName,
      urlHost: host,
      success,
      statusCode: statusCode ?? null,
      ...properties,
    }, {
      durationMs,
    });
  }

  trackAddressResolutionResult(
    resolutionSource: 'cache_memory' | 'cache_storage' | 'inflight_shared' | 'google_geocode_api',
    durationMs: number,
    resultType: 'ok' | 'zero_results' | 'non_us' | 'error' | 'maps_not_ready' | 'empty_address' | 'cache_hit',
    apiCalled: boolean,
    properties: TelemetryProps = {}
  ): void {
    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    this.debugLog(
      `🔎 TELEMETRY-DEBUG addressResolution source=${resolutionSource} apiCalled=${apiCalled} resultType=${resultType} durationMs=${Math.round(durationMs)}`,
      properties
    );

    this.trackEvent('address_resolution_result', {
      resolutionSource,
      resultType,
      apiCalled,
      ...properties,
    }, {
      durationMs,
    });
  }

  startTimer(): number {
    if (this.windowService.window?.performance?.now) {
      return this.windowService.window.performance.now();
    }

    return Date.now();
  }

  stopTimer(startedAt: number): number {
    const now = this.startTimer();
    return Math.max(0, now - startedAt);
  }

  getDebugModeLabel(): 'none' | 'telemetry' | 'developer' | 'both' {
    const telemetry = this.isTelemetryDebugActiveFromQuery();
    const developer = this.isDeveloperMode();

    if (telemetry && developer) return 'both';
    if (telemetry) return 'telemetry';
    if (developer) return 'developer';
    return 'none';
  }

  isTelemetryDebugEnabled(): boolean {
    if (!environment.telemetry?.enableDebugMode) {
      return false;
    }

    if (!this.isTelemetrySegmentEnabled()) {
      return false;
    }

    return this.isDeveloperMode() || this.isTelemetryDebugActiveFromQuery();
  }

  private initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    if (!this.isTelemetrySegmentEnabled()) {
      return;
    }

    const telemetryConfig = this.resolveTelemetryConnectionString();
    const connectionString = telemetryConfig.connectionString;
    // Telemetry: print initialization target details for debug sessions.
    this.logTelemetryConfiguration(connectionString, telemetryConfig.source);
    if (!connectionString || !this.windowService.window) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        extensions: [this.clickAnalyticsPlugin],
        extensionConfig: {
          [this.clickAnalyticsPlugin.identifier]: {
            autoCapture: false,
            useDefaultContentNameOrId: true,
            parentDataTag: 'parentid',
            dataTags: {
              useDefaultContentNameOrId: true,
              customDataPrefix: 'data-',
            },
          },
        },
      },
    });

    this.appInsights.loadAppInsights();
  }

  // Telemetry: diagnostic output to verify the active App Insights target.
  private resolveTelemetryConnectionString(): { connectionString: string; source: 'runtime' | 'environment' | 'none' } {
    const runtimeConnectionString = (this.windowService.window as TelemetryWindow | null)?.__TELEMETRY_CONFIG__?.appInsightsConnectionString?.trim();
    if (runtimeConnectionString) {
      return { connectionString: runtimeConnectionString, source: 'runtime' };
    }

    const environmentConnectionString = environment.telemetry?.appInsightsConnectionString?.trim();
    if (environmentConnectionString) {
      return { connectionString: environmentConnectionString, source: 'environment' };
    }

    return { connectionString: '', source: 'none' };
  }

  private logTelemetryConfiguration(connectionString?: string, source: 'runtime' | 'environment' | 'none' = 'none'): void {
    const instrumentationKey = this.extractInstrumentationKey(connectionString || '');
    const maskedKey = this.maskKey(instrumentationKey);
    const host = this.windowService.window?.location?.host || '';

    this.debugLog(
      `🔎 TELEMETRY-DEBUG config key=${maskedKey} hasConnectionString=${Boolean(connectionString)} source=${source} appEnv=${environment.production ? 'prod' : 'dev'} host=${host}`
    );
  }

  private extractInstrumentationKey(connectionString: string): string {
    const parts = connectionString.split(';');
    const keyPart = parts.find(part => part.trim().toLowerCase().startsWith('instrumentationkey='));
    if (!keyPart) {
      return '';
    }

    const key = keyPart.split('=')[1] || '';
    return key.trim();
  }

  private maskKey(key: string): string {
    if (!key) {
      return 'empty';
    }

    if (key.length <= 8) {
      return key;
    }

    return `${key.slice(0, 4)}...${key.slice(-4)}`;
  }

  private withContext(properties: TelemetryProps): { [key: string]: string } {
    const merged = {
      ...this.commonContext,
      ...this.clientContext,
      ...properties,
      debugMode: this.getDebugModeLabel(),
    };
    const normalized: { [key: string]: string } = {};
    Object.entries(merged).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      normalized[key] = value === null ? '' : String(value);
    });
    return normalized;
  }

  private buildTelemetrySummary(properties: Record<string, string>, measurements: TelemetryMeasurements = {}): string {
    const summaryKeys = [
      'apiName',
      'landingPageId',
      'landingPageTypeId',
      'titleMain',
      'offerId',
      'offerThreadId',
      'recordId',
      'recordsForSMSReservedAutoID',
      'statusCode',
      'success',
      'resultType',
      'resolutionSource',
      'apiCalled',
      'thanksSubId',
      'campaign',
      'subId',
      'journeyStage',
      'pageSequence',
      'thankYouProvider',
      'thankYouType',
      'clickTarget',
      'clickSurface',
      'offerTitle',
      'offerBrand',
      'sourceId',
      'offerRank',
      'fieldKey',
      'controlType',
      'interactionType',
      'valueBucket',
      'fromStepTitle',
      'toStepTitle',
      'direction',
      'transitionSource',
      'isSkip',
      'skipReason',
      'timeOnStepMs',
      'fromStepFieldKey',
      'fromStepFieldType',
      'fromStepValueBucket',
      'fromStepValueChanged',
      'stepTitle',
      'stepSlug',
      'googleApiKeyLast6',
    ];
    const parts: string[] = [];

    summaryKeys.forEach(key => {
      const value = properties[key];
      if (value) {
        parts.push(`${key}=${value}`);
      }
    });

    Object.entries(measurements).forEach(([key, value]) => {
      parts.push(`${key}=${Math.round(value)}`);
    });

    return parts.length ? ` ${parts.join(' ')}` : '';
  }

  private isDeveloperMode(): boolean {
    return this.readTruthyQueryFlag('developer');
  }

  private isTelemetryDebugActiveFromQuery(): boolean {
    const paramName = environment.telemetry?.debugQueryParamName || 'debugTelemetry';
    return this.readTruthyQueryFlag(paramName);
  }

  private isTelemetrySegmentEnabled(): boolean {
    const paramName = environment.telemetry?.segmentQueryParamName || 'telemetrySegment';
    return this.readQueryValue(paramName) === 'true';
  }

  private readTruthyQueryFlag(param: string): boolean {
    const value = this.readQueryValue(param);
    return value === '1' || value === 'true' || value === 'yes';
  }

  private readQueryValue(param: string): string | null {
    const search = this.windowService.locationSearch;
    if (!search) {
      return null;
    }

    const qp = new URLSearchParams(search);
    return qp.get(param)?.trim().toLowerCase() || null;
  }

  private getHost(url: string): string {
    try {
      return new URL(url).host;
    } catch {
      return '';
    }
  }

  // Telemetry: normalize browser/device/os dimensions for low-cardinality analytics.
  private buildClientContext(): TelemetryProps {
    const ua = (this.windowService.window?.navigator?.userAgent || '').toLowerCase();
    if (!ua) {
      return {
        uaFamily: 'other',
        deviceClass: 'other',
        osFamily: 'other',
      };
    }

    return {
      uaFamily: this.resolveUaFamily(ua),
      deviceClass: this.resolveDeviceClass(ua),
      osFamily: this.resolveOsFamily(ua),
    };
  }

  private resolveUaFamily(ua: string): string {
    if (ua.includes('edg/')) return 'edge';
    if (ua.includes('firefox/')) return 'firefox';
    if (ua.includes('chrome/') || ua.includes('crios/')) return 'chrome';
    if (ua.includes('safari/') && !ua.includes('chrome/') && !ua.includes('crios/')) return 'safari';
    return 'other';
  }

  private resolveDeviceClass(ua: string): string {
    if (ua.includes('bot') || ua.includes('spider') || ua.includes('crawl')) return 'bot';
    if (ua.includes('tablet') || ua.includes('ipad')) return 'tablet';
    if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) return 'mobile';
    return 'desktop';
  }

  private resolveOsFamily(ua: string): string {
    if (ua.includes('windows')) return 'windows';
    if (ua.includes('android')) return 'android';
    if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ios')) return 'ios';
    if (ua.includes('mac os') || ua.includes('macintosh')) return 'macos';
    if (ua.includes('linux')) return 'linux';
    return 'other';
  }

  private debugLog(message: string, ...args: unknown[]): void {
    if (!this.isTelemetryDebugEnabled()) {
      return;
    }

    const isTelemetryMessage = message.includes('TELEMETRY-');
    if (!isTelemetryMessage) {
      return;
    }

    console.log(message, ...args);
  }
}
